import { useState, type FormEvent } from 'react';
import Modal, { inputCls, labelCls } from '@/components/base/Modal';
import { saveSize, updateSize, type Size } from '@/services/sizes';

interface SizeModalProps {
  size?: Size | null;
  onClose: () => void;
  onSaved: () => void;
}

const emptyForm = { name: '', description: '', cmRange: '', sortOrder: '0', isActive: true, weightKg: '0.5', sizeType: 'premium' };

export default function SizeModal({ size, onClose, onSaved }: SizeModalProps) {
  const isEditing = !!size;
  const [form, setForm] = useState(() =>
    size
      ? { name: size.name, description: size.description, cmRange: size.cmRange, sortOrder: String(size.sortOrder), isActive: size.isActive, weightKg: String(size.weightKg), sizeType: size.sizeType }
      : emptyForm,
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  function set<K extends keyof typeof emptyForm>(key: K, value: (typeof emptyForm)[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    if (!form.name.trim()) {
      setError('El nombre de la talla es obligatorio.');
      return;
    }
    if (!(Number(form.weightKg) > 0)) {
      setError('El peso aproximado (kg) es obligatorio y debe ser mayor a 0.');
      return;
    }

    setSaving(true);
    const payload = { ...form, sortOrder: Number(form.sortOrder) || 0, weightKg: Number(form.weightKg) || 0 };
    const { error: saveError } = isEditing
      ? await updateSize(size!.id, payload)
      : await saveSize(payload);
    setSaving(false);

    if (saveError) {
      setError(saveError);
      return;
    }
    onSaved();
    onClose();
  }

  return (
    <Modal
      title={isEditing ? 'Editar talla' : 'Nueva Talla'}
      subtitle={
        isEditing
          ? `Modificando: ${size!.name}`
          : 'Registra una talla configurable para el catálogo.'
      }
      onClose={onClose}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2.5">
            <i className="ri-error-warning-line"></i>
            <span>{error}</span>
          </div>
        )}

        <div>
          <label className={labelCls}>Nombre *</label>
          <input className={inputCls} value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="7" />
        </div>
        <div>
          <label className={labelCls}>Talla (aprox.)</label>
          <input className={inputCls} value={form.cmRange} onChange={(e) => set('cmRange', e.target.value)} placeholder="37 - 40" />
          <p className="text-xs text-foreground-400 mt-1">Se muestra en la tienda para orientar al cliente.</p>
        </div>
        <div>
          <label className={labelCls}>Descripción</label>
          <input className={inputCls} value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="34-36 · Mascota mediana" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Peso aprox. (kg) *</label>
            <input
              type="number"
              min="0"
              step="0.01"
              className={inputCls}
              value={form.weightKg}
              onChange={(e) => set('weightKg', e.target.value)}
              placeholder="0.5"
            />
            <p className="text-xs text-foreground-400 mt-1">Se usa para calcular el flete de Interrapidísimo.</p>
          </div>
          <div>
            <label className={labelCls}>Tipo de talla *</label>
            <select className={inputCls} value={form.sizeType} onChange={(e) => set('sizeType', e.target.value)}>
              <option value="premium">Premium</option>
              <option value="basica">Básica</option>
            </select>
            <p className="text-xs text-foreground-400 mt-1">El producto solo verá las tallas de su propia línea.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Orden de visualización</label>
            <input type="number" min="0" className={inputCls} value={form.sortOrder} onChange={(e) => set('sortOrder', e.target.value)} />
          </div>
          <div className="flex items-end pb-1">
            <label className="flex items-center gap-2 text-sm text-foreground-700 cursor-pointer">
              <input type="checkbox" checked={form.isActive} onChange={(e) => set('isActive', e.target.checked)} className="w-4 h-4 accent-accent-500 cursor-pointer" />
              Activa en catálogo
            </label>
          </div>
        </div>

        <div className="flex gap-3 pt-1">
          <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 text-sm border border-background-300 text-foreground-700 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer">
            Cancelar
          </button>
          <button type="submit" disabled={saving} className="flex-1 px-4 py-2.5 text-sm font-semibold bg-accent-500 text-white rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed">
            {saving ? (<><i className="ri-loader-4-line animate-spin mr-1.5"></i>Guardando...</>) : (<><i className="ri-save-line mr-1.5"></i>{isEditing ? 'Guardar cambios' : 'Crear talla'}</>)}
          </button>
        </div>
      </form>
    </Modal>
  );
}